import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Data Catalog", to: "/data-catalog" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#05070A]/80 border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 sm:h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="DestinyCS home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-cyan-400" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:08842355449"
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-cyan-400 transition-colors"
          >
            <Phone size={15} />
            <span className="font-mono-tech text-xs">0884-2355449</span>
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group flex items-center gap-1.5 bg-cyan-400 text-[#05070A] text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.45)] transition-all duration-300"
          >
            Request Data Demo
            <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-[#05070A]/95"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `text-base font-medium ${isActive ? "text-cyan-400" : "text-slate-200"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-cta-button"
                className="mt-2 flex items-center justify-center gap-2 bg-cyan-400 text-[#05070A] font-semibold px-5 py-3 rounded-full"
              >
                Request Data Demo <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
